import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { loadStoredAuth } from '../authStorage';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M11.5 6.5A5 5 0 1 1 9 2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function AnalyticsStat({ label, value, tone = 'default' }) {
  return (
    <article className={`analytics-stat analytics-stat--${tone}`}>
      <span className="analytics-stat-label">{label}</span>
      <strong className="analytics-stat-value">{value}</strong>
    </article>
  );
}

function KpiTile({ label, value }) {
  return (
    <div className="analytics-kpi-tile">
      <span className="analytics-kpi-label">{label}</span>
      <strong className="analytics-kpi-value">{value ?? '—'}</strong>
    </div>
  );
}

export function AnalyticsPage() {
  const navigate = useNavigate();
  const auth = loadStoredAuth();
  const token = auth?.token;

  const [summary, setSummary] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  const loadAnalytics = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [summaryData, kpiData] = await Promise.all([
        apiFetch('/api/dashboard/summary', { token }),
        apiFetch('/api/dashboard/kpis', { token }),
      ]);
      setSummary(summaryData || null);
      setKpis(kpiData || null);
    } catch (e) {
      setError(e.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics, refreshKey]);

  const categoryEntries = useMemo(
    () => Object.entries(summary?.categoryStats || {}),
    [summary],
  );

  const topCategories = categoryEntries.sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0)).slice(0, 6);

  if (loading) {
    return (
      <section className="analytics-page">
        <Skeleton lines={10} />
      </section>
    );
  }

  return (
    <section className="analytics-page">
      <div className="analytics-header">
        <div>
          <span className="analytics-eyebrow">Performance</span>
          <h1 className="analytics-heading">Analytics</h1>
          <p className="analytics-subheading">
            KPI snapshots, revenue visibility, and operational throughput built from live dashboard data.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setRefreshKey((value) => value + 1)}>
          <RefreshIcon />
          Refresh
        </Button>
      </div>

      {error ? <ErrorState message={error} onRetry={loadAnalytics} /> : null}

      {summary ? (
        <>
          <div className="analytics-stats-grid">
            <AnalyticsStat label="Total Jobs" value={summary.totalJobs ?? 0} />
            <AnalyticsStat label="Customers" value={summary.totalCustomers ?? 0} tone="accent" />
            <AnalyticsStat label="Revenue" value={`$${Number(summary.totalRevenue || 0).toFixed(2)}`} tone="info" />
            <AnalyticsStat label="Pending Revenue" value={`$${Number(summary.pendingRevenue || 0).toFixed(2)}`} tone="warning" />
          </div>

          <div className="analytics-layout">
            <Card title="Operational KPIs">
              {kpis ? (
                <div className="analytics-kpi-grid">
                  <KpiTile label="On-Time Completion" value={`${kpis.sla?.onTimeCompletionRatePct ?? 0}%`} />
                  <KpiTile label="Overdue Open Jobs" value={kpis.sla?.overdueOpen ?? 0} />
                  <KpiTile label="Jobs Due Soon" value={kpis.sla?.dueSoonOpen ?? 0} />
                  <KpiTile label="Avg Resolution (hrs)" value={kpis.operations?.avgResolutionHours ?? 0} />
                  <KpiTile label="Check-In Compliance" value={`${kpis.operations?.checkinCompliancePct ?? 0}%`} />
                  <KpiTile label="Completion Proof" value={`${kpis.sla?.completionProofCoveragePct ?? 0}%`} />
                </div>
              ) : (
                <EmptyState title="No KPI data" message="KPI payload is unavailable right now." />
              )}
            </Card>

            <Card title="Jobs Snapshot">
              <div className="analytics-snapshot-list">
                <div className="analytics-snapshot-row"><span>New</span><strong>{summary.newJobs ?? 0}</strong></div>
                <div className="analytics-snapshot-row"><span>Assigned</span><strong>{summary.assignedJobs ?? 0}</strong></div>
                <div className="analytics-snapshot-row"><span>In Progress</span><strong>{summary.inProgressJobs ?? 0}</strong></div>
                <div className="analytics-snapshot-row"><span>Completed</span><strong>{summary.completedJobs ?? 0}</strong></div>
                <div className="analytics-snapshot-row"><span>Invoices</span><strong>{summary.totalInvoices ?? 0}</strong></div>
                <div className="analytics-snapshot-row"><span>Pending Invoices</span><strong>{summary.pendingInvoices ?? 0}</strong></div>
              </div>
            </Card>

            <Card title="Top Categories">
              {topCategories.length ? (
                <div className="analytics-category-list">
                  {topCategories.map(([name, count]) => (
                    <div className="analytics-category-row" key={name}>
                      <span>{name}</span>
                      <strong>{count}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState title="No category breakdown" message="Category stats are not available in the current summary." />
              )}
            </Card>
          </div>
        </>
      ) : (
        <ErrorState title="No analytics data" message="Summary data is unavailable." />
      )}
    </section>
  );
}
